import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  schoolYearsApi,
  SchoolYear,
  CreateSchoolYearData,
  UpdateSchoolYearData,
} from '@/api/school-years';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, CalendarDays, Loader2 } from 'lucide-react';

export function SchoolYearFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [startingDate, setStartingDate] = useState('');
  const [endingDate, setEndingDate] = useState('');

  useEffect(() => {
    if (id) {
      fetchSchoolYear(parseInt(id));
    }
  }, [id]);

  const fetchSchoolYear = async (schoolYearId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data: SchoolYear = await schoolYearsApi.getById(schoolYearId);
      setCode(data.code);
      setStartingDate(data.startingDate || '');
      setEndingDate(data.endingDate || '');
    } catch (err) {
      setError('Erreur lors du chargement de l\'année fiscale');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = (start: string, end: string): string => {
    if (!start || !end) return '';
    const startYear = new Date(start).getFullYear();
    const endYear = new Date(end).getFullYear();
    return `${startYear}-${endYear}`;
  };

  const handleStartingDateChange = (value: string) => {
    setStartingDate(value);
    if (!isEditing && value && endingDate) {
      setCode(generateCode(value, endingDate));
    }
  };

  const handleEndingDateChange = (value: string) => {
    setEndingDate(value);
    if (!isEditing && startingDate && value) {
      setCode(generateCode(startingDate, value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError('Le code est requis');
      return;
    }

    if (!startingDate) {
      setError('La date de rentree est requise');
      return;
    }

    if (!endingDate) {
      setError('La date de fin est requise');
      return;
    }

    if (new Date(startingDate) >= new Date(endingDate)) {
      setError('La date de rentree doit etre anterieure a la date de fin');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        const updateData: UpdateSchoolYearData = {
          startingDate,
          endingDate,
        };
        await schoolYearsApi.update(parseInt(id), updateData);
      } else {
        const createData: CreateSchoolYearData = {
          code,
          startingDate,
          endingDate,
        };
        await schoolYearsApi.create(createData);
      }

      navigate('/school-years');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Erreur lors de la sauvegarde de l\'année fiscale';
      setError(errorMessage);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/school-years">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <CalendarDays className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isEditing ? 'Modifier l\'année fiscale' : 'Nouvelle année fiscale'}
            </h1>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="h-4 w-4 text-primary" />
              Informations de l'année fiscale
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 max-w-xl">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Ex: 2024-2025"
                  disabled={isEditing}
                  className={isEditing ? 'bg-muted' : ''}
                />
                {isEditing && (
                  <p className="text-xs text-muted-foreground">
                    Le code ne peut pas etre modifie
                  </p>
                )}
                {!isEditing && (
                  <p className="text-xs text-muted-foreground">
                    Le code est genere automatiquement a partir des dates
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startingDate">Date de rentree *</Label>
                  <Input
                    id="startingDate"
                    type="date"
                    value={startingDate}
                    onChange={(e) => handleStartingDateChange(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endingDate">Date de fin *</Label>
                  <Input
                    id="endingDate"
                    type="date"
                    value={endingDate}
                    onChange={(e) => handleEndingDateChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note :</strong> La creation ou modification d'une année fiscale
                  sera automatiquement synchronisée avec toutes les écoles de la plateforme.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/school-years')}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Enregistrer
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
